import { useState } from "react";
import cx from "classnames";
import { faCircleCheck, faCircleInfo, faTruckFast } from "@fortawesome/pro-regular-svg-icons";

import Icon from "common/components/Icon";
import LinkButton from "common/components/LinkButton";
import {
  ClientCartDetail,
  FreeShippingProgress as FreeShippingProgressData,
  FreeShippingStatus,
} from "common/types/carts";
import { Optional } from "common/utils/types";

import { urls } from "client/routes";
import { useFeatures } from "client/clinics/hooks";
import { FreeShippingProgressSurface } from "client/analytics/types";
import { useFreeShippingProgressViewOnVisible } from "client/analytics/utils";

import { useIsCartUpdating } from "../../hooks";
import {
  getFreeShipmentLine,
  getFreeShippingHelperText,
  getFreeShippingMessage,
  getPaidShipmentDisclaimer,
} from "../../utils";

import styles from "../../styles/shared/FreeShippingProgress.module.less";

/**
 * The cart's free shipping progress, held at its last settled value while the cart is updating so
 * the banner doesn't flicker through intermediate states.
 */
const useSettledFreeShipping = (freeShipping: Optional<FreeShippingProgressData>) => {
  const isCartUpdating = useIsCartUpdating();
  const [settled, setSettled] = useState(freeShipping);

  // Setting state during render is React's pattern for state that follows a prop. React
  // re-renders before paint, so no effect is needed.
  if (!isCartUpdating && settled !== freeShipping) {
    setSettled(freeShipping);
  }

  return isCartUpdating ? settled : freeShipping;
};

type FreeShippingProgressCardProps = {
  cart: ClientCartDetail;
  freeShipping: FreeShippingProgressData;
  message: string;
  surface: FreeShippingProgressSurface;
  className?: string;
  /** Adds a "Shop deals" link while the cart can still unlock free shipping */
  hasShopLink?: boolean;
  /** Called when the shop link is followed, e.g. to close the drawer it sits in */
  onNavigate?: () => void;
};

const FreeShippingProgressCard = ({
  cart,
  freeShipping,
  message,
  surface,
  className,
  hasShopLink = false,
  onNavigate,
}: FreeShippingProgressCardProps) => {
  const ref = useFreeShippingProgressViewOnVisible({ cart, freeShipping, surface });

  const isInProgress = freeShipping.status === FreeShippingStatus.InProgress;
  const helperText = getFreeShippingHelperText(freeShipping);
  const hasSupportingLines =
    !!freeShipping.free_shipments.length || !!freeShipping.paid_shipments.length;

  // The lines are a live region and the message is announced by the status region beside the
  // card; the helper text is static context, so a cart dropping back below the threshold doesn't
  // re-read it. Keys use the fulfiller name because the backend sends one shipment per fulfiller.
  return (
    <div ref={ref} className={cx(styles.container, className)}>
      <div className={styles.content}>
        <div className={cx(styles.message, !isInProgress && styles.qualified)}>
          <Icon icon={isInProgress ? faTruckFast : faCircleCheck} className={styles.icon} />
          {message}
        </div>
        <div aria-hidden={true} className={styles.track}>
          <div className={styles.fill} style={{ width: `${freeShipping.progress_percent}%` }} />
        </div>
        {helperText && <p className={styles.helperText}>{helperText}</p>}
        <div aria-live="polite">
          {hasSupportingLines && (
            <ul className={styles.lines}>
              {freeShipping.free_shipments.map((freeShipment) => (
                <li key={`free-${freeShipment.fulfiller_name}`} className={styles.line}>
                  <Icon icon={faCircleCheck} className={cx(styles.icon, styles.freeIcon)} />
                  {getFreeShipmentLine(freeShipment)}
                </li>
              ))}
              {freeShipping.paid_shipments.map((paidShipment) => (
                <li key={`paid-${paidShipment.fulfiller_name}`} className={styles.line}>
                  <Icon icon={faCircleInfo} className={cx(styles.icon, styles.infoIcon)} />
                  {getPaidShipmentDisclaimer(paidShipment)}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {hasShopLink && isInProgress && (
        <LinkButton dest={urls.products.deals()} className={styles.link} onClick={onNavigate}>
          Shop deals
        </LinkButton>
      )}
    </div>
  );
};

type FreeShippingProgressProps = Omit<FreeShippingProgressCardProps, "freeShipping" | "message">;

const SettledFreeShippingProgress = ({ cart, ...cardProps }: FreeShippingProgressProps) => {
  const freeShipping = useSettledFreeShipping(cart.free_shipping);
  const message = freeShipping ? getFreeShippingMessage(freeShipping) : null;

  // The status region stays mounted while the card comes and goes, because a live region only
  // announces changes made after it mounts. The card mounts only when it has something to show,
  // so its view observer attaches to a rendered element.
  return (
    <>
      <div role="status" className={styles.status}>
        {message}
      </div>
      {freeShipping && message && (
        <FreeShippingProgressCard
          cart={cart}
          freeShipping={freeShipping}
          message={message}
          {...cardProps}
        />
      )}
    </>
  );
};

/**
 * Display-only banner for `cart.free_shipping`. The backend decides the status, the amounts and
 * which shipments still charge; this maps them to copy and never compares amounts itself. The flag
 * check sits in its own component so a clinic with the flag off never subscribes to cart updates.
 */
const FreeShippingProgress = (props: FreeShippingProgressProps) => {
  const features = useFeatures();

  if (!features.FREE_SHIPPING_PROGRESS) {
    return null;
  }

  return <SettledFreeShippingProgress {...props} />;
};

export default FreeShippingProgress;
